import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import { IMedia } from "../../entities/IMedia";
import ProductService from "../../services/MediaService";
import { getErrorMessage, getMediaSrc } from "../../services/apiUtils";
import { theme } from "../../theme";

const SLIDE_ENTITY_ID = 4;

const sectionStyle = {
  backgroundColor: "white",
  borderRadius: "12px",
  boxShadow:
    "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
  overflow: "hidden",
};

const headerStyle = {
  background: "linear-gradient(135deg, #303398 0%, #4F55D6 100%)",
  color: "#fff",
  px: 2.5,
  py: 1.75,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1.25,
  flexWrap: "wrap" as const,
};

export const SlideImageForm = () => {
  const [imageInfos, setImageInfos] = useState<Array<IMedia>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>("");
  const [uploading, setUploading] = useState<boolean>(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [brokenIds, setBrokenIds] = useState<Set<number>>(new Set());
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const notify = (message: string, severity: "success" | "error" = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const loadImages = () => {
    setLoading(true);
    setLoadError("");
    return ProductService.getMediaByProduct(SLIDE_ENTITY_ID)
      .then((images) => {
        if (!isMounted.current) return;
        setImageInfos(images);
        setBrokenIds(new Set());
      })
      .catch((err: any) => {
        if (!isMounted.current) return;
        setLoadError(getErrorMessage(err, "Could not load images."));
      })
      .finally(() => {
        if (isMounted.current) setLoading(false);
      });
  };

  useEffect(() => {
    loadImages();

  }, []);

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = "";
    if (files.length === 0) return;

    setUploading(true);
    Promise.all(
      files.map(
        (file) =>
          new Promise<void>((resolve, reject) => {
            ProductService.upload(file, SLIDE_ENTITY_ID, () => {})
              .then(() => resolve())
              .catch((err: any) => reject(err));
          })
      )
    )
      .then(() => loadImages())
      .then(() => notify("Image(s) uploaded successfully."))
      .catch((err: any) => notify(getErrorMessage(err, "Some images could not be uploaded."), "error"))
      .finally(() => {
        if (isMounted.current) setUploading(false);
      });
  };

  const handleRemove = (img: IMedia) => {
    setRemovingId(img.mediaId);
    const prevList = imageInfos;
    setImageInfos((prev) => prev.filter((item) => item.mediaId !== img.mediaId));

    ProductService.deleteMedia(String(SLIDE_ENTITY_ID), img.mediaId)
      .then(() => {
        notify(`${img.filename || "Image"} removed.`);
      })
      .catch((err: any) => {
        setImageInfos(prevList);
        notify(getErrorMessage(err, "Could not remove image."), "error");
      })
      .finally(() => {
        if (isMounted.current) setRemovingId(null);
      });
  };

  const markBroken = (mediaId: number) => {
    setBrokenIds((prev) => {
      const next = new Set(prev);
      next.add(mediaId);
      return next;
    });
  };

  return (
    <Box sx={sectionStyle}>
      <Box sx={headerStyle}>
        <Box display="flex" alignItems="center" gap={1.25}>
          <PhotoLibraryOutlinedIcon />
          <Typography variant="subtitle1" fontWeight={600}>
            Slide Images ({imageInfos.length})
          </Typography>
        </Box>
        <Button
          component="label"
          size="small"
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <CloudUploadRoundedIcon />}
          disabled={uploading}
          sx={{ borderRadius: "10px", color: "#fff", borderColor: "rgba(255,255,255,0.7)" }}
        >
          {uploading ? "Uploading..." : "Upload Images"}
          <input type="file" hidden multiple accept="image/*" onChange={handleUpload} />
        </Button>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These banners appear in the storefront's homepage slider. Upload wide images for the
          best fit, and remove any you no longer need.
        </Typography>

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 3 }}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">
              Loading images...
            </Typography>
          </Box>
        ) : !loadError && imageInfos.length === 0 ? (
          <Box
            sx={{
              border: `1px dashed ${theme.palette.grey?.[400] || "#ccc"}`,
              borderRadius: "10px",
              py: 5,
              textAlign: "center",
              bgcolor: "#faf9fc",
            }}
          >
            <PhotoLibraryOutlinedIcon sx={{ fontSize: 36, color: "#ccc", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No slide images yet. Click "Upload Images" above to add your first banner.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {imageInfos.map((img) => {
              const isBroken = brokenIds.has(img.mediaId);
              const isRemoving = removingId === img.mediaId;
              return (
                <Grid item xs={12} sm={6} md={4} key={img.mediaId}>
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: "10px",
                      overflow: "hidden",
                      border: `1px solid ${theme.palette.grey?.[300] || "#eee"}`,
                      height: 160,
                      bgcolor: "#faf9fc",
                    }}
                  >
                    {isBroken ? (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                          color: "text.secondary",
                        }}
                      >
                        <BrokenImageOutlinedIcon sx={{ fontSize: 32, color: "#ccc" }} />
                        <Typography variant="caption" sx={{ px: 1, textAlign: "center" }}>
                          Preview unavailable
                        </Typography>
                      </Box>
                    ) : (
                      <img
                        src={getMediaSrc(img)}
                        alt={img.filename || "Slide image"}
                        onError={() => markBroken(img.mediaId)}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    )}

                    <Box
                      sx={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                      }}
                    >
                      <Tooltip title="Remove image">
                        <span>
                          <IconButton
                            size="small"
                            disabled={isRemoving}
                            sx={{ bgcolor: "rgba(255,255,255,0.9)", "&:hover": { bgcolor: "#fff" } }}
                            onClick={() => handleRemove(img)}
                          >
                            {isRemoving ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteOutlineRoundedIcon fontSize="small" color="error" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>

                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: "rgba(0,0,0,0.55)",
                        color: "#fff",
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {img.filename}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
